import { toast } from 'react-toastify';
import {
  extractUnassignedOcrService,
  matchUnassignedNotesService,
  saveUnassignedNotesService,
  assignUnassignedReturnService,
} from '../../../../services/unassignedReturns.service';
import { callUnassigned, isStaleStatus, notifyError } from './unassignedUtils';

export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_NOTES_LENGTH = 20000;
const ALLOWED_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'heic', 'pdf']);
const ALLOWED_MIME = /^(image\/(jpeg|png|heic)|application\/pdf)$/;

const isAllowedFile = (file) => {
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  return ALLOWED_EXTENSIONS.has(ext) || ALLOWED_MIME.test(file.type);
};

export const getFileError = (file) => {
  if (!isAllowedFile(file)) return 'Unsupported file type. Use JPG, PNG, HEIC or PDF.';
  return file.size > MAX_FILE_BYTES ? 'File is too large. Maximum size is 10 MB.' : null;
};

export const buildExtractForm = (packageId, useExistingImage, file) => {
  const formData = new FormData();
  formData.append('packageId', packageId);
  formData.append('useExistingImage', useExistingImage ? 'true' : 'false');
  if (file) formData.append('image', file);
  return formData;
};

export const getImagePreview = (previewUrl, useExistingImage, row) => {
  if (previewUrl) return previewUrl;
  return useExistingImage ? (row?.imageUrl || row?.thumbnailUrl) : null;
};

// Assign needs a fresh match when the notes changed, or a chosen seller when nothing matched.
export const getAssignState = ({ match, notes, matchedNotes, seller }) => {
  const isMatched = Boolean(match?.isMatch);
  const isStale = isMatched && notes !== matchedNotes;
  const hasSeller = Boolean(seller.companyId && seller.sellerId);
  let label = 'Assign Manually & Move';
  if (isMatched) label = match.canAutoAssign ? 'Assign & Move to Assigned Returns' : 'Confirm & Assign';
  return { isMatched, isStale, canAssign: isMatched ? !isStale : Boolean(match) && hasSeller, label };
};

export const buildAssignPayload = ({ row, match, seller, notes }) => {
  if (match?.isMatch) {
    return { packageId: row.PackageId, assignmentType: 'Ocr', assignedCompanyId: match.companyId, assignedSellerId: match.sellerId, notes };
  }
  return {
    packageId: row.PackageId,
    assignmentType: 'Manual',
    assignedCompanyId: seller.companyId,
    assignedSellerId: seller.sellerId,
    notes: notes.trim() ? notes : null,
  };
};

const applyExtraction = (entity, { setNotes, setMatch, setMatchedNotes }) => {
  if (entity?.success === false) {
    toast.error(entity.error || 'Text could not be read from the image');
    return;
  }
  const text = entity?.text || '';
  setNotes(text);
  setMatch(entity?.match || null);
  setMatchedNotes(text);
  if (!text) toast.info('No text was found in the image');
};

export const extractText = async (ctx) => {
  const { row, file, useExistingImage, markDirty } = ctx;
  if (!file && !useExistingImage) {
    toast.warning('Upload an image or use the existing package image');
    return;
  }
  const { ok, body, message } = await callUnassigned(
    () => extractUnassignedOcrService(buildExtractForm(row.PackageId, useExistingImage, file)),
    'Failed to extract text'
  );
  if (!ok) {
    notifyError(message);
    return;
  }
  markDirty();
  applyExtraction(body.Entity, ctx);
};

export const rematchNotes = async ({ row, notes, setMatch, setMatchedNotes }) => {
  if (!notes.trim()) return;
  const { ok, body, message } = await callUnassigned(
    () => matchUnassignedNotesService(row.PackageId, notes),
    'Failed to match notes'
  );
  if (!ok || !body.Entity) {
    notifyError(message);
    return;
  }
  setMatch(body.Entity);
  setMatchedNotes(notes);
};

export const saveNotes = async ({ row, notes, onHide }) => {
  const { ok, body, message } = await callUnassigned(
    () => saveUnassignedNotesService({ packageId: row.PackageId, notes }),
    'Failed to save notes'
  );
  if (ok) {
    toast.success('Notes saved. The package stays in Unassigned Returns.');
    onHide(true);
    return;
  }
  notifyError(message);
  if (isStaleStatus(body?.Status)) onHide(true);
};

export const assignPackage = async (ctx) => {
  const { ok, body, message } = await callUnassigned(
    () => assignUnassignedReturnService(buildAssignPayload(ctx)),
    'Failed to assign package'
  );
  if (ok && body.Entity?.assigned !== false) {
    if (body.Metadata?.chargeCreated === false) {
      toast.warning('Package assigned, but the seller charge could not be created.');
    }
    toast.success('Assigned. The package moved to Assigned Returns.');
    ctx.onAssigned();
    return;
  }
  notifyError(ok ? 'Failed to assign package' : message);
  if (isStaleStatus(body?.Status)) ctx.onHide(true);
};
